// ContextBolt SEO's and ContextBolt Bookmarks' widgets: one type each, with a view for each thing it shows. Each
// view reads one of the reads in shared/oursReads.ts, filled by Connect's choose step (src/panels/connect/OursWidgets.tsx).
// Every number is one the product sent: a value it did not give shows as a dash, never as 0. Sizes are in em, from
// the fits in plans.ts (oursFit), so a view fills its tile at any size.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowDownRight, ArrowLeft, ArrowUpRight, Bookmark, Coins, Globe2, LineChart, ListOrdered, Minus, Plug, Search, Sparkles, Tags, TriangleAlert } from "lucide-react";
import { formatNumber, shortAgo, timeAgo } from "../../shared/format";
import { savedByDay, shareText, type AiVisibility, type Credits, type Found, type FoundList, type Keywords, type Library, type OurData, type QuickWins, type Research, type Saved, type SearchWeek, type Topic, type Topics } from "../../shared/oursReads";
import type { Stream, WidgetItem } from "../../shared/types";
import { useSize } from "../charts/charts";
import { api } from "../lib/api";
import { cx } from "../lib/util";
import { set, useStore } from "../state/store";
import type { WP } from "./data";
import { quarter } from "./layout";
import { oursFit, type OursFit } from "./plans";
import { DAY } from "../../shared/period";

/** Each view: its words in Connect's choose step and on an empty tile, its read and its first size. */
export const OUR_VIEWS = {
  seo: {
    week: { title: "Search this week", line: "Clicks, impressions, click rate and position, with fourteen days of clicks, from ContextBolt SEO's nightly snapshot", read: "seo-search", size: [480, 320], icon: LineChart },
    wins: { title: "Quick wins", line: "Searches you rank just off page one for, from the quick wins you last asked ContextBolt SEO for", read: "seo-wins", size: [440, 320], icon: ListOrdered },
    ai: { title: "AI visibility", line: "Your share of AI answers on your topic, against your rivals, from your latest report", read: "seo-ai", size: [480, 300], icon: Sparkles },
    keywords: { title: "Keywords", line: "The keywords you checked: searches a month, difficulty and the year's trend", read: "seo-keywords", size: [440, 320], icon: Search },
    research: { title: "Recent research", line: "Your latest lookups of every kind, with what each found", read: "seo-research", size: [400, 320], icon: ListOrdered },
    credits: { title: "Credits", line: "How many credits you have, and when this month's allowance resets", read: "seo-credits", size: [280, 192], icon: Coins },
  },
  bookmarks: {
    recent: { title: "Recent saves", line: "What you saved lately from X, Reddit, LinkedIn and the web, newest first", read: "bm-library", size: [400, 340], icon: Bookmark },
    lately: { title: "Saved lately", line: "How much you saved this week and the week before, by day and by where from", read: "bm-library", size: [400, 280], icon: LineChart },
    topics: { title: "Your topics", line: "The topics your bookmarks are sorted into. Open one to see what is in it", read: "bm-topics", size: [360, 320], icon: Tags },
    search: { title: "Search your bookmarks", line: "Find anything you saved, by meaning, without leaving the board", read: "bm-topics", size: [400, 340], icon: Search },
  },
} as const;

export type SeoView = keyof typeof OUR_VIEWS.seo;
export type BookmarksView = keyof typeof OUR_VIEWS.bookmarks;
export const SERVICE_OF = { seo: "contextbolt-seo", bookmarks: "contextbolt-bookmarks" } as const;

export function viewOfItem(item: Pick<WidgetItem, "type" | "props">): { type: "seo" | "bookmarks"; view: string; spec: (typeof OUR_VIEWS)["seo"][SeoView] | (typeof OUR_VIEWS)["bookmarks"][BookmarksView] } | null {
  if (item.type !== "seo" && item.type !== "bookmarks") return null;
  const views = OUR_VIEWS[item.type] as Record<string, (typeof OUR_VIEWS)["seo"][SeoView]>;
  const v = typeof item.props.view === "string" && Object.hasOwn(views, item.props.view) ? item.props.view : Object.keys(views)[0];
  return { type: item.type, view: v, spec: views[v] };
}

// ---------------------------------------------------------------------------
// pieces every view shares

const num = (v: number | null | undefined, digits = 0) => (typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-US", { maximumFractionDigits: digits, minimumFractionDigits: digits }) : "—");
const compact = (v: number | null | undefined) => (typeof v === "number" && Number.isFinite(v) ? formatNumber(v, Math.abs(v) >= 10000 ? { style: "compact" } : null) : "—");
const pct = (v: number | null | undefined, digits = 1) => (typeof v === "number" && Number.isFinite(v) ? `${(v * 100).toFixed(digits)}%` : "—");
const day = (ymd: string | null | undefined) => (ymd ? new Date(ymd + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : "");

/** A change: up or down, in the color that says whether that is good. Nothing at all when the product gave none. */
function Delta({ v, text, good, label }: { v: number | null | undefined; text: string; good: "up" | "down"; label?: string }) {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const flat = v === 0;
  const better = flat ? null : good === "up" ? v > 0 : v < 0;
  const Icon = flat ? Minus : v > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cx("st-chip", better === true && "is-good", better === false && "is-bad")}>
      <Icon strokeWidth={2.4} />
      {text}
      {label && <span>{label}</span>}
    </span>
  );
}

/** A change in percent from the number now and its change, both as the product gave them; the change alone when the before was 0. */
function changeText(now: number | null | undefined, d: number | null | undefined): string {
  if (typeof now !== "number" || typeof d !== "number") return "";
  const before = now - d;
  if (before > 0) {
    const p = Math.abs((d / before) * 100);
    return `${p >= 10 ? p.toFixed(0) : p.toFixed(1)}%`;
  }
  return num(Math.abs(d));
}

/** A small label over a part of a view, as AI usage's are. */
const Kicker = ({ children }: { children: ReactNode }) => <span className="oc-k">{children}</span>;

/** Where a platform's save came from, as a small mark. */
function Platform({ p, host }: { p: string; host?: string | null }) {
  const mark = p === "twitter" ? "𝕏" : p === "reddit" ? "r/" : p === "linkedin" ? "in" : null;
  return (
    <span className={cx("oc-plat", `is-${p}`)} title={p === "twitter" ? "X" : p === "web" ? (host ?? "The web") : p[0].toUpperCase() + p.slice(1)}>
      {mark ?? <Globe2 />}
    </span>
  );
}

/** A view's own empty state: what it is waiting for, in words, and the one way on. */
function Waiting({ icon, title, sub, children }: { icon: ReactNode; title: string; sub: string; children?: ReactNode }) {
  return (
    <div className="empty oc-empty">
      <span className="oc-empty-ico">{icon}</span>
      <span className="empty-title">{title}</span>
      <span className="empty-sub">{sub}</span>
      {children && <div className="empty-actions">{children}</div>}
    </div>
  );
}

function openConnect(item: WidgetItem) {
  const type = item.type === "bookmarks" ? "bookmarks" : "seo";
  set({ panel: "connect", panelArg: { tab: "data", service: SERVICE_OF[type], item: item.id, stream: item.stream ?? null } });
}

/** Before the first read: not connected, reading, or the reason it could not read. A shared view says only that there is nothing yet. */
export function OursEmpty({ item, stream, readOnly }: { item: WidgetItem; stream: Stream | null; readOnly: boolean }) {
  const v = viewOfItem(item);
  const name = item.type === "bookmarks" ? "ContextBolt Bookmarks" : "ContextBolt SEO";
  const Icon = v?.spec.icon ?? Plug;
  if (readOnly) return <Waiting icon={<Icon />} title={v?.spec.title ?? name} sub="Nothing to show yet." />;
  if (stream?.error)
    return (
      <Waiting icon={<TriangleAlert />} title="Could not read it" sub={stream.error}>
        <button className="btn btn-sm" onClick={() => openConnect(item)} data-interactive>
          Open {name}
        </button>
      </Waiting>
    );
  if (item.stream)
    return (
      <div className="empty oc-empty">
        <span className="spinner" />
        <span className="empty-sub">Reading {name}…</span>
      </div>
    );
  return (
    <Waiting icon={<Icon />} title={v?.spec.title ?? name} sub={v?.spec.line ?? ""}>
      <button className="btn btn-sm btn-primary" onClick={() => openConnect(item)} data-interactive>
        <Plug size={13} /> Connect {name}
      </button>
    </Waiting>
  );
}

/** The line under a view: whose numbers, and when the product made them. */
function Foot({ children }: { children: ReactNode }) {
  return <div className="oc-foot">{children}</div>;
}

function Frame({ fit, view, children, scroll, innerRef }: { fit: OursFit; view: string; children: ReactNode; scroll?: boolean; innerRef: (el: HTMLDivElement | null) => void }) {
  return (
    <div ref={innerRef} className={cx("oc", `oc-v-${view}`, `is-${fit.id}`)} style={fit.px ? { fontSize: quarter(fit.px) } : undefined} data-scroll={scroll ? "" : undefined}>
      {children}
    </div>
  );
}

function Wrong({ what }: { what: string }) {
  return <Waiting icon={<TriangleAlert />} title="This source does not fit" sub={`This widget shows ${what}, and its source gives something else. Connect it again from Connect.`} />;
}

// ---------------------------------------------------------------------------
// ContextBolt SEO

export function SeoW(p: WP) {
  const v = viewOfItem(p.item);
  const data = p.stream?.json as OurData | undefined;
  switch (v?.view) {
    case "wins":
      return data?.read === "seo-wins" ? <WinsView {...p} d={data} /> : <Wrong what="quick wins" />;
    case "ai":
      return data?.read === "seo-ai" ? <AiView {...p} d={data} /> : <Wrong what="AI visibility" />;
    case "keywords":
      return data?.read === "seo-keywords" ? <KeywordsView {...p} d={data} /> : <Wrong what="your keywords" />;
    case "research":
      return data?.read === "seo-research" ? <ResearchView {...p} d={data} /> : <Wrong what="your research" />;
    case "credits":
      return data?.read === "seo-credits" ? <CreditsView {...p} d={data} /> : <Wrong what="your credits" />;
    default:
      return data?.read === "seo-search" ? <WeekView {...p} d={data} /> : <Wrong what="your search week" />;
  }
}

/** Clicks a day, as bars: the last seven in the accent, the seven before quieter. Bars only for days the product gave. */
function DayBars({ values, split, labels }: { values: (number | null)[]; split?: number; labels?: [string, string] | null }) {
  const max = Math.max(1, ...values.map((x) => x ?? 0));
  return (
    <div className="oc-bars-wrap">
      <div className="oc-bars" role="img" aria-label={values.map((x) => (x === null ? "not read" : String(x))).join(", ")}>
        {values.map((x, i) => (
          <span key={i} className={cx("oc-bar", split !== undefined && i >= split && "is-now", x === null && "is-unknown")} style={{ height: x === null ? "100%" : `${Math.max(2, (x / max) * 100)}%` }} title={x === null ? "Not read" : num(x)} />
        ))}
      </div>
      {labels && (
        <div className="oc-bars-axis">
          <span>{labels[0]}</span>
          <span>{labels[1]}</span>
        </div>
      )}
    </div>
  );
}

function WeekView({ d }: WP & { d: SearchWeek }) {
  const [ref, size] = useSize<HTMLDivElement>();
  if (d.empty)
    return <Waiting icon={<LineChart />} title="No search snapshot yet" sub="ContextBolt SEO takes one each night once Search Console is connected in it." />;
  const series = d.series ?? [];
  const fit = oursFit("week", size, { queries: (d.queries ?? []).length, insight: (d.insights ?? []).length > 0, bars: series.length >= 2 });
  // the fourteen days end on the week's last day, when SEO gave all fourteen
  const labels: [string, string] | null = series.length === 14 && d.from && d.to ? [day(new Date(Date.parse(d.to + "T00:00:00Z") - 13 * DAY).toISOString().slice(0, 10)), day(d.to)] : null;
  const prior = d.clicks !== null && d.clicks !== undefined && d.dClicks !== null && d.dClicks !== undefined;
  return (
    <Frame fit={fit} view="week" innerRef={ref}>
      <div className="oc-hero">
        <b className="oc-big">{num(d.clicks)}</b>
        <span className="oc-big-of">clicks</span>
        {prior ? <Delta v={d.dClicks} text={changeText(d.clicks, d.dClicks)} good="up" label="vs the week before" /> : <span className="oc-muted">{d.from && d.to ? `${day(d.from)} to ${day(d.to)}` : "the last seven days"}</span>}
      </div>
      {fit.id !== "stat" && (
        <div className="oc-stats">
          <div>
            <Kicker>Impressions</Kicker>
            <b>{compact(d.impressions)}</b>
            <Delta v={d.dImpressions} text={changeText(d.impressions, d.dImpressions)} good="up" />
          </div>
          <div>
            <Kicker>Click rate</Kicker>
            <b>{pct(d.ctr)}</b>
            <Delta v={d.dCtr} text={`${num(Math.abs(d.dCtr ?? 0), 1)} pts`} good="up" />
          </div>
          <div>
            <Kicker>Position</Kicker>
            <b>{num(d.position, 1)}</b>
            <Delta v={d.dPosition} text={num(Math.abs(d.dPosition ?? 0), 1)} good="up" />
          </div>
        </div>
      )}
      {series.length >= 2 && fit.id !== "bare" && <DayBars values={series} split={series.length === 14 ? 7 : undefined} labels={fit.id === "stat" ? null : labels} />}
      {fit.id === "full" && (d.queries ?? []).length > 0 && (
        <div className="oc-queries">
          <Kicker>Queries to watch</Kicker>
          {d.queries!.slice(0, fit.rows).map((q) => (
            <div key={q.query} className="oc-q">
              <span className={cx("oc-tag", `is-${q.tag}`)}>{q.tag === "new" ? "New" : q.tag === "gap" ? "Seen, not clicked" : "Top"}</span>
              <span className="oc-q-name">{q.query}</span>
              <span className="oc-q-n">{compact(q.impressions)}</span>
              <span className="oc-q-pos">#{num(q.position, 1)}</span>
            </div>
          ))}
        </div>
      )}
      {fit.id === "full" && fit.insight && d.insights?.[0] && <p className="oc-insight">{d.insights[0]}</p>}
      {fit.id !== "stat" && fit.id !== "bare" && <Foot>{d.site ? siteName(d.site) : "Search Console"} · {d.from && d.to ? `${day(d.from)} to ${day(d.to)}` : "the last seven days"} · from ContextBolt SEO{d.at ? `, ${timeAgo(d.at)}` : ""}</Foot>}
    </Frame>
  );
}

const siteName = (s: string) => s.replace(/^sc-domain:/, "").replace(/^https?:\/\//, "").replace(/\/(?=\s|$)/, "");

/** Where a position sits, on a short track from 1 to 20 with page one's edge marked. */
function Track({ pos }: { pos: number | null }) {
  if (pos === null) return <span className="oc-track is-unknown" />;
  const x = Math.min(1, Math.max(0, (pos - 1) / 19));
  return (
    <span className="oc-track" title={`Position ${pos.toFixed(1)}`}>
      <span className="oc-track-edge" />
      <span className={cx("oc-track-dot", pos <= 10.5 ? "is-near" : "is-far")} style={{ left: `${x * 100}%` }} />
    </span>
  );
}

function WinsView({ d }: WP & { d: QuickWins }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const rows = d.rows ?? [];
  const fit = oursFit("wins", size, { rows: rows.length });
  if (d.empty === "none") return <Waiting icon={<ListOrdered />} title="No quick wins saved yet" sub="Ask your AI for your Search Console quick wins with ContextBolt SEO. The newest list shows here." />;
  if (d.empty === "gsc-off") return <Waiting icon={<Plug />} title="Connect Search Console in ContextBolt SEO" sub="Your quick wins show here once ContextBolt SEO can read your Search Console." />;
  if (d.empty === "gsc-pick") return <Waiting icon={<Globe2 />} title="Choose a site in ContextBolt SEO" sub="Your Google account has more than one Search Console site. Make one the default in ContextBolt SEO and this fills in." />;
  if (d.empty === "gsc-none") return <Waiting icon={<Globe2 />} title="No verified site in Search Console" sub="Verify your site in Google Search Console, then ContextBolt SEO can read it." />;
  if (!rows.length) return <Waiting icon={<ListOrdered />} title="Nothing just off page one" sub={`No search sat between positions 5 and 20 with ${d.min ? `${num(d.min)} impressions` : "enough impressions"} or more from ${day(d.from)} to ${day(d.to)}.`} />;
  return (
    <Frame fit={fit} view="wins" innerRef={ref} scroll>
      <div className="oc-rows">
        <div className="oc-row oc-row-head">
          <span>Search</span>
          {fit.id !== "narrow" && <span className="oc-track-head">Position</span>}
          <span>Seen</span>
        </div>
        {rows.map((r) => (
          <div key={r.query} className="oc-row" title={`${r.query}: position ${num(r.position, 1)}, ${num(r.impressions)} impressions, ${num(r.clicks)} clicks, ${pct(r.ctr)} click rate`}>
            <span className="oc-row-main">
              <b>{r.query}</b>
              <span>
                #{num(r.position, 1)} · {num(r.clicks)} clicks · {pct(r.ctr)}
              </span>
            </span>
            {fit.id !== "narrow" && <Track pos={r.position} />}
            <span className="oc-row-n">{compact(r.impressions)}</span>
          </div>
        ))}
      </div>
      <Foot>
        {d.site ? siteName(d.site) : ""} · {day(d.from)} to {day(d.to)} · saved {d.at ? timeAgo(d.at) : "in ContextBolt SEO"}
      </Foot>
    </Frame>
  );
}

function AiView({ d }: WP & { d: AiVisibility }) {
  const [ref, size] = useSize<HTMLDivElement>();
  if (d.empty) return <Waiting icon={<Sparkles />} title="No AI visibility report yet" sub="When you run an AI visibility report with ContextBolt SEO, your share of AI answers and your rivals show here." />;
  const sov = d.sov ?? [];
  const fit = oursFit("ai", size, { rivals: sov.length, check: d.kind === "check", rows: (d.pages ?? []).length });
  if (d.kind === "check")
    return (
      <Frame fit={fit} view="ai" innerRef={ref}>
        <div className="oc-hero">
          <b className="oc-big">{num(d.mentions)}</b>
          <span className="oc-big-of">AI answers mention {d.domain ?? "you"}</span>
        </div>
        {d.topic && <span className="oc-muted">On “{d.topic}”</span>}
        <Foot>From your latest AI visibility check{d.at ? `, ${timeAgo(d.at)}` : ""}</Foot>
      </Frame>
    );
  const share = typeof d.share === "number" ? d.share : null;
  const top = Math.max(1, ...sov.map((x) => x.share ?? 0));
  const facts = [
    d.mentions !== null && d.mentions !== undefined ? (
      <span key="m">
        <b>{num(d.mentions)}</b>
        {d.answers !== null && d.answers !== undefined ? ` of ${num(d.answers)}` : ""} answers name you
      </span>
    ) : null,
    d.engines ? (
      <span key="e">
        Google AI <b>{num(d.engines.google)}</b>, ChatGPT <b>{num(d.engines.chatgpt)}</b>
      </span>
    ) : null,
    fit.id === "full" && d.cited !== null && d.cited !== undefined ? (
      <span key="c">
        Cited <b>{num(d.cited)}</b> times, on {num(d.pagesCited)} of your pages
      </span>
    ) : null,
  ].filter(Boolean);
  return (
    <Frame fit={fit} view="ai" innerRef={ref}>
      <div className="oc-ai">
        <div className="oc-ai-main">
          <div className="oc-hero">
            <b className="oc-big">{share === null ? "—" : shareText(share)}</b>
            <span className="oc-big-of">of AI answers</span>
          </div>
          <div className="oc-ai-meta">
            {d.rank !== null && d.rank !== undefined && d.rankOf ? (
              <span className={cx("oc-rank", d.rank === 1 && "is-first")}>
                #{d.rank} <i>of {d.rankOf}</i>
              </span>
            ) : null}
            {d.leader && d.rank !== 1 && <span className="oc-muted">{d.leader} leads</span>}
          </div>
          {fit.id !== "stat" && facts.length > 0 && <div className="oc-ai-facts">{facts}</div>}
          {fit.insight && (d.pages ?? []).length > 0 && (
            <div className="oc-pages">
              <Kicker>Your pages AI answers cite</Kicker>
              {d.pages!.map((p) => (
                <div key={p.path} className="oc-page">
                  <span>{p.path}</span>
                  <b>{num(p.cites)}</b>
                </div>
              ))}
            </div>
          )}
        </div>
        {fit.id !== "stat" && sov.length > 0 && (
          <div className="oc-sov">
            <Kicker>Share of voice</Kicker>
            {sov.slice(0, fit.rows).map((x) => (
              <div key={x.brand} className={cx("oc-sov-row", x.you && "is-you")}>
                <span className="oc-sov-name">{x.brand}</span>
                <span className="oc-sov-bar">
                  <span style={{ width: `${Math.max(2, ((x.share ?? 0) / top) * 100)}%` }} />
                </span>
                <span className="oc-sov-n">{x.share === null ? "—" : shareText(x.share)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {fit.id !== "stat" && (
        <Foot>
          {d.topic ? `“${d.topic}”` : "Your topic"}
          {d.location ? `, ${d.location}` : ""} · the last twelve months{d.at ? ` · report ${timeAgo(d.at)}` : ""}
        </Foot>
      )}
    </Frame>
  );
}

/** Difficulty, 0 to 100, as SEO tools band it: easy under 30, hard from 60. */
const hardness = (kd: number | null) => (kd === null ? "unknown" : kd < 30 ? "easy" : kd < 60 ? "mid" : "hard");

function KeywordsView({ d }: WP & { d: Keywords }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const rows = [...d.rows].sort((a, b) => Number(b.pinned) - Number(a.pinned) || (b.volume ?? -1) - (a.volume ?? -1));
  const fit = oursFit("keywords", size, { rows: rows.length });
  if (!rows.length) return <Waiting icon={<Search />} title="No keywords checked yet" sub="Each keyword you check for difficulty with ContextBolt SEO shows here, with its searches a month and how hard it is to rank for." />;
  return (
    <Frame fit={fit} view="keywords" innerRef={ref} scroll>
      <div className="oc-rows">
        <div className="oc-row oc-row-head">
          <span>Keyword</span>
          {fit.id !== "narrow" && <span>Difficulty</span>}
          <span>A month</span>
        </div>
        {rows.map((r) => (
          <div key={`${r.keyword}|${r.location ?? ""}`} className="oc-row" title={`${r.keyword}${r.location ? ` (${r.location})` : ""}: ${num(r.volume)} searches a month, difficulty ${num(r.difficulty)}${r.trend !== null ? `, ${r.trend > 0 ? "+" : ""}${num(r.trend)}% on last year` : ""}${r.cpc !== null ? `, $${num(r.cpc, 2)} a click` : ""}`}>
            <span className="oc-row-main">
              <b>{r.keyword}</b>
              <span>
                {r.trend !== null ? <em className={r.trend > 0 ? "is-up" : r.trend < 0 ? "is-down" : ""}>{`${r.trend > 0 ? "↑" : r.trend < 0 ? "↓" : ""}${num(Math.abs(r.trend))}% on last year`}</em> : (r.intent ?? "")}
                {fit.id === "narrow" && r.difficulty !== null ? ` · difficulty ${num(r.difficulty)}` : ""}
              </span>
            </span>
            {fit.id !== "narrow" && (
              <span className={cx("oc-kd", `is-${hardness(r.difficulty)}`)}>
                <span className="oc-kd-bar">
                  <span style={{ width: `${r.difficulty ?? 0}%` }} />
                </span>
                <span className="oc-kd-n">{num(r.difficulty)}</span>
              </span>
            )}
            <span className="oc-row-n">{compact(r.volume)}</span>
          </div>
        ))}
      </div>
      <Foot>Keyword checks you saved in ContextBolt SEO</Foot>
    </Frame>
  );
}

function ResearchView({ d }: WP & { d: Research }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const fit = oursFit("research", size, { rows: d.rows.length });
  if (!d.rows.length) return <Waiting icon={<ListOrdered />} title="No research saved yet" sub="Everything you look up with ContextBolt SEO is saved to your account, and the newest shows here." />;
  return (
    <Frame fit={fit} view="research" innerRef={ref} scroll>
      <div className="oc-feed">
        {d.rows.map((r, i) => (
          <div key={i} className="oc-feed-row">
            <span className="oc-feed-top">
              <span className="oc-chip">{r.kind}</span>
              <span className="oc-feed-at">{r.at ? shortAgo(r.at) : ""}</span>
            </span>
            <b className="oc-feed-label">{siteName(r.label.replace(/^(keyword ideas|ranked keywords|competitors|domain|SERP|AI visibility report|AI visibility): /, ""))}</b>
            {r.line && fit.id !== "narrow" && <span className="oc-feed-line">{r.line}</span>}
          </div>
        ))}
      </div>
    </Frame>
  );
}

function CreditsView({ d }: WP & { d: Credits }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const fit = oursFit("credits", size, {});
  const total = Math.max(1, d.allowance + d.topup);
  const resets = d.resets ? new Date(d.resets + "T12:00:00Z") : null;
  return (
    <Frame fit={fit} view="credits" innerRef={ref}>
      <div className="oc-hero">
        <b className="oc-big">{num(d.total)}</b>
        <span className="oc-big-of">credits</span>
      </div>
      {fit.id !== "bare" && (
        <div className="oc-split" role="img" aria-label={`${num(d.allowance)} from this month's allowance, ${num(d.topup)} in top-ups`}>
          <span className="oc-split-a" style={{ flexGrow: d.allowance / total }} />
          {d.topup > 0 && <span className="oc-split-b" style={{ flexGrow: d.topup / total }} />}
        </div>
      )}
      <div className="oc-credit-lines">
        <span>
          <i className="oc-sw is-a" />
          <b>{num(d.allowance)}</b> this month{resets ? `, resets ${resets.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}` : ""}
        </span>
        {fit.id !== "bare" && (
          <span>
            <i className="oc-sw is-b" />
            <b>{num(d.topup)}</b> top-up, never expire
          </span>
        )}
      </div>
    </Frame>
  );
}

// ---------------------------------------------------------------------------
// ContextBolt Bookmarks

export function BookmarksW(p: WP) {
  const v = viewOfItem(p.item);
  const data = p.stream?.json as OurData | undefined;
  switch (v?.view) {
    case "lately":
      return data?.read === "bm-library" ? <LatelyView {...p} d={data} /> : <Wrong what="what you saved lately" />;
    case "topics":
      return data?.read === "bm-topics" ? <TopicsView {...p} d={data} /> : <Wrong what="your topics" />;
    case "search":
      return data?.read === "bm-topics" ? <SearchView {...p} d={data} /> : <Wrong what="a search of your bookmarks" />;
    default:
      return data?.read === "bm-library" ? <RecentView {...p} d={data} /> : <Wrong what="your recent saves" />;
  }
}

const labelOf = (topics: Topic[], id: string | null) => (id ? (topics.find((t) => t.id === id)?.label ?? null) : null);

function SaveRow({ s, topic, compactRow }: { s: Saved; topic: string | null; compactRow: boolean }) {
  const inner = (
    <>
      <Platform p={s.platform} host={s.host} />
      <span className="oc-save-main">
        <b>{s.title || s.url}</b>
        {!compactRow && (
          <span className="oc-save-meta">
            {s.author || s.host}
            {topic ? <span className="oc-chip">{topic}</span> : null}
          </span>
        )}
      </span>
      <span className="oc-save-at">{s.at ? shortAgo(s.at) : ""}</span>
    </>
  );
  return s.url ? (
    <a className="oc-save" href={s.url} target="_blank" rel="noreferrer noopener" data-interactive title={s.title}>
      {inner}
    </a>
  ) : (
    <div className="oc-save">{inner}</div>
  );
}

function RecentView({ d }: WP & { d: Library }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const fit = oursFit("recent", size, { rows: d.items.length });
  if (!d.items.length) return <Waiting icon={<Bookmark />} title="Nothing saved yet" sub="What you save with ContextBolt Bookmarks from X, Reddit, LinkedIn and the web shows here, newest first." />;
  return (
    <Frame fit={fit} view="recent" innerRef={ref} scroll>
      <div className="oc-saves">
        {d.items.slice(0, 40).map((s) => (
          <SaveRow key={s.id} s={s} topic={labelOf(d.topics, s.topic)} compactRow={fit.id === "narrow"} />
        ))}
      </div>
      {fit.id !== "narrow" && <Foot>{num(d.total)} bookmarks in your library</Foot>}
    </Frame>
  );
}

function LatelyView({ d }: WP & { d: Library }) {
  const [ref, size] = useSize<HTMLDivElement>();
  if (!d.total && !d.items.length) return <Waiting icon={<LineChart />} title="Nothing saved yet" sub="How much you save each day shows here once ContextBolt Bookmarks has your first saves." />;
  const by = savedByDay(d);
  const fit = oursFit("lately", size, {});
  const now = Date.now();
  const thisWeek = d.items.filter((x) => x.at !== null && x.at >= now - 7 * DAY);
  const where = Object.entries(
    thisWeek.reduce<Record<string, number>>((m, x) => {
      m[x.platform] = (m[x.platform] ?? 0) + 1;
      return m;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const topicCount = thisWeek.reduce<Record<string, number>>((m, x) => (x.topic ? { ...m, [x.topic]: (m[x.topic] ?? 0) + 1 } : m), {});
  const topTopic = Object.entries(topicCount).sort((a, b) => b[1] - a[1])[0];
  const change = by.weekFull && by.beforeFull ? by.week - by.before : null;
  const first = new Date(now - 13 * DAY).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return (
    <Frame fit={fit} view="lately" innerRef={ref}>
      <div className="oc-hero">
        <b className="oc-big">
          {by.weekFull ? "" : "at least "}
          {num(by.week)}
        </b>
        <span className="oc-big-of">saved in the last 7 days</span>
        {change !== null && fit.id !== "bare" ? <Delta v={change} text={num(Math.abs(change))} good="up" label={`vs ${num(by.before)} the week before`} /> : null}
      </div>
      {fit.id !== "bare" && <DayBars values={by.counts} split={7} labels={fit.id === "stat" ? null : [first, "Today"]} />}
      {fit.id === "full" && (
        <div className="oc-lately-facts">
          {where.length > 0 && (
            <span className="oc-where">
              {where.map(([p, n]) => (
                <span key={p}>
                  <Platform p={p} /> {n}
                </span>
              ))}
            </span>
          )}
          {topTopic && labelOf(d.topics, topTopic[0]) && (
            <span className="oc-muted">
              Most saved: <b>{labelOf(d.topics, topTopic[0])}</b>
            </span>
          )}
        </div>
      )}
      {fit.id !== "bare" && fit.id !== "stat" && <Foot>{num(d.total)} bookmarks in your library</Foot>}
    </Frame>
  );
}

/** Asks one of ours for a search or a topic, on the owner's connection; a shared view never can. */
function useAsk(stream: Stream | null) {
  const conn = useStore((s) => {
    const pull = stream?.pullId ? s.pulls[stream.pullId] : undefined;
    return pull?.config.connection ?? null;
  });
  const [state, setState] = useState<{ busy: boolean; error: string | null; found: FoundList | null }>({ busy: false, error: null, found: null });
  const seq = useRef(0);
  const run = async (ask: "bm-search" | "bm-topic", args: Record<string, unknown>) => {
    if (!conn) return;
    const n = ++seq.current;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const r = await api<{ found: FoundList }>(`/api/connections/${encodeURIComponent(conn)}/ask`, { body: { ask, args } });
      if (n === seq.current) setState({ busy: false, error: null, found: r.found });
    } catch (e) {
      if (n === seq.current) setState((s) => ({ ...s, busy: false, error: (e as Error).message }));
    }
  };
  return { conn, ...state, run, clear: () => setState({ busy: false, error: null, found: null }) };
}

function FoundRows({ items }: { items: Found[] }) {
  return (
    <div className="oc-saves">
      {items.map((f, i) => {
        const inner = (
          <>
            <Platform p={["twitter", "reddit", "linkedin"].includes(f.source) ? f.source : "web"} host={f.source} />
            <span className="oc-save-main">
              <b>{f.title || f.url}</b>
              <span className="oc-save-meta">
                {f.author}
                {f.tags[0] ? <span className="oc-chip">{f.tags[0]}</span> : null}
              </span>
            </span>
            <span className="oc-save-at">{f.saved ?? ""}</span>
          </>
        );
        return f.url ? (
          <a key={i} className="oc-save" href={f.url} target="_blank" rel="noreferrer noopener" data-interactive title={f.excerpt}>
            {inner}
          </a>
        ) : (
          <div key={i} className="oc-save">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

function TopicsView({ stream, readOnly, d }: WP & { d: Topics }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const ask = useAsk(stream);
  const fit = oursFit("topics", size, { rows: d.topics.length });
  if (!d.topics.length) return <Waiting icon={<Tags />} title="No topics yet" sub="Your topics show here once ContextBolt Bookmarks has sorted your saves into them." />;
  if (ask.found || ask.busy || ask.error)
    return (
      <Frame fit={fit} view="topics" innerRef={ref} scroll>
        <button className="oc-back" onClick={ask.clear} data-interactive>
          <ArrowLeft /> {ask.found ? ask.found.label : "Your topics"}
        </button>
        {ask.busy ? <span className="oc-muted">Opening the topic…</span> : ask.error ? <span className="oc-error">{ask.error}</span> : ask.found && (ask.found.items.length ? <FoundRows items={ask.found.items} /> : <span className="oc-muted">Nothing in this topic yet.</span>)}
      </Frame>
    );
  const max = Math.max(1, ...d.topics.map((t) => t.count ?? 0));
  const topics = [...d.topics].sort((a, b) => (b.count ?? -1) - (a.count ?? -1));
  const open = !readOnly && !!ask.conn;
  return (
    <Frame fit={fit} view="topics" innerRef={ref} scroll>
      <div className="oc-topics">
        {topics.map((t) => {
          const body = (
            <>
              <span className="oc-topic-bar" style={{ width: `${Math.max(3, ((t.count ?? 0) / max) * 100)}%` }} />
              <span className="oc-topic-main">
                <b>{t.label}</b>
                {t.description && fit.id === "full" && <span>{t.description}</span>}
              </span>
              <span className="oc-row-n">{num(t.count)}</span>
            </>
          );
          return open ? (
            <button key={t.id} className="oc-topic" onClick={() => void ask.run("bm-topic", { topic: t.id })} title={`Open ${t.label}`} data-interactive>
              {body}
            </button>
          ) : (
            <div key={t.id} className="oc-topic">
              {body}
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

function SearchView({ stream, readOnly, d }: WP & { d: Topics }) {
  const [ref, size] = useSize<HTMLDivElement>();
  const ask = useAsk(stream);
  const [q, setQ] = useState("");
  const fit = oursFit("search", size, {});
  const go = (query: string) => {
    const t = query.trim();
    if (t) void ask.run("bm-search", { query: t });
  };
  useEffect(() => {
    if (!q) ask.clear();
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const can = !readOnly && !!ask.conn;
  return (
    <Frame fit={fit} view="search" innerRef={ref} scroll>
      <form
        className="oc-search"
        onSubmit={(e) => {
          e.preventDefault();
          go(q);
        }}
        data-interactive
      >
        <Search />
        <input value={q} disabled={!can} placeholder={can ? "Search by meaning, like “pricing advice”" : "Search is for the board's owner"} aria-label="Search your bookmarks" onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
        {ask.busy && <span className="spinner" />}
      </form>
      {ask.error ? (
        <span className="oc-error">{ask.error}</span>
      ) : ask.found ? (
        ask.found.items.length ? (
          <FoundRows items={ask.found.items} />
        ) : (
          <span className="oc-muted">Nothing saved matches “{ask.found.label}”.</span>
        )
      ) : (
        d.topics.length > 0 &&
        fit.id !== "narrow" && (
          <div className="oc-try">
            <Kicker>Or start from a topic</Kicker>
            <div className="oc-try-chips">
              {d.topics.slice(0, 8).map((t) => (
                <button
                  key={t.id}
                  className="oc-chip is-button"
                  disabled={!can}
                  onClick={() => {
                    setQ(t.label);
                    go(t.label);
                  }}
                  data-interactive
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )
      )}
    </Frame>
  );
}
